import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { sileo } from 'sileo'
import { useApp } from '../../context/AppContext'
import useUser from '../../hooks/useUser'
import { employeeName } from '../../utils/employee'
import AuthAccessValidator from '../Auth/AuthAccessValidator'

/**
 * Sidebar con el shell del Design System (SiteFlow), el mismo que usan
 * Proyectos, Operaciones, Talento Humano y Documentos: blanca en claro, azul
 * tinta en oscuro, plegable a iconos y con las secciones en acordeón.
 *
 * Las clases (.sidebar-gm, .sidebar-workspace, .nav-link-gm, .sidebar-user…)
 * salen de styles/gm-app-shell.css, copia literal del Design System: no se
 * redefinen acá para que un cambio allá se propague copiando el archivo.
 *
 * @component
 * @param {Object}   props
 * @param {boolean}  [props.isOpen=false]    - Cajón abierto en móvil.
 * @param {boolean}  [props.collapsed=false] - Modo icono en escritorio.
 * @param {Function} [props.onClose]         - Cierra el cajón en móvil.
 * @returns {JSX.Element}
 */
const Sidebar = ({ isOpen = false, collapsed = false, onClose }) => {
  const { logout, user, employee: currentEmployee } = useApp()
  const { employee } = useUser(user?.id, 'Auth')
  const location = useLocation()
  const navigate = useNavigate()
  const actual = location.pathname

  /** La sección desplegada; se recuerda igual que en las demás apps. */
  const [grupoAbierto, setGrupoAbierto] = useState(() => {
    try {
      return localStorage.getItem('sidebar-open-group') || 'grpTickets'
    } catch {
      return 'grpTickets'
    }
  })

  const esActivo = (ruta, exacto = false) => {
    if (exacto) return actual === ruta
    if (ruta === '/') return actual === '/'
    return actual === ruta || actual.startsWith(ruta + '/')
  }

  /**
   * El menú es configuración, no markup (igual que $GM_NAV en las apps PHP):
   * agregar una pantalla es agregar una línea acá.
   * `permiso: true` marca lo que sólo ve el equipo de soporte.
   */
  const NAV = [
    {
      tipo: 'links',
      items: [{ to: '/', icon: 'fa-house', label: 'Inicio', exacto: true }],
    },
    {
      tipo: 'grupo',
      id: 'grpTickets',
      titulo: 'Tickets',
      items: [
        {
          to: '/tickets',
          icon: 'fa-chart-simple',
          label: 'Todos los tickets',
          permiso: true,
        },
        { to: '/mis-tickets', icon: 'fa-list-ul', label: 'Mis tickets' },
        {
          to: '/reportes',
          icon: 'fa-file-lines',
          label: 'Reportes',
          permiso: true,
        },
      ],
    },
    {
      tipo: 'grupo',
      id: 'grpSeguridad',
      titulo: 'Seguridad',
      items: [
        {
          to: '/reportar-incidente',
          icon: 'fa-shield-halved',
          label: 'Reportar incidente',
        },
        {
          to: '/incidentes',
          icon: 'fa-clipboard-list',
          label: 'Incidentes',
          permiso: true,
        },
      ],
    },
    {
      tipo: 'grupo',
      id: 'grpRecursos',
      titulo: 'Recursos',
      items: [
        { to: '/soluciones', icon: 'fa-lightbulb', label: 'Soluciones' },
        { to: '/ayuda', icon: 'fa-circle-question', label: 'Centro de ayuda' },
      ],
    },
    {
      tipo: 'grupo',
      id: 'grpSistema',
      titulo: 'Sistema',
      items: [
        {
          to: '/configuracion',
          icon: 'fa-gear',
          label: 'Configuración',
          permiso: true,
        },
      ],
    },
  ]

  // La sección que contiene la pantalla actual manda sobre la recordada.
  useEffect(() => {
    const dueño = NAV.find(
      (b) => b.tipo === 'grupo' && b.items.some((i) => esActivo(i.to))
    )
    if (dueño && dueño.id !== grupoAbierto) setGrupoAbierto(dueño.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actual])

  const abrirGrupo = (id) => {
    const siguiente = grupoAbierto === id ? null : id
    setGrupoAbierto(siguiente)
    try {
      if (siguiente) localStorage.setItem('sidebar-open-group', siguiente)
      else localStorage.removeItem('sidebar-open-group')
    } catch {
      /* sin almacenamiento: vale sólo para esta sesión */
    }
  }

  const cerrarSesion = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (err) {
      console.error('Error cerrando sesión:', err)
      sileo.error({ title: 'No se pudo cerrar sesión' })
    }
  }

  const enlace = (item, sub = false) => {
    const el = (
      <Link
        key={item.to}
        to={item.to}
        onClick={onClose}
        className={`nav-link-gm ${sub ? 'nav-link-sub ' : ''}${esActivo(item.to, item.exacto) ? 'active' : ''}`}
        title={collapsed ? item.label : undefined}
      >
        <i className={`fas ${item.icon} nav-icon`}></i>
        <span className="nav-label">{item.label}</span>
      </Link>
    )
    if (!item.permiso) return el
    return (
      <AuthAccessValidator
        key={item.to}
        AccessNames={['ticket_support']}
        showError={false}
      >
        {el}
      </AuthAccessValidator>
    )
  }

  const nombre =
    employeeName(employee) ||
    employeeName(currentEmployee) ||
    user?.user_metadata?.name ||
    'Usuario'
  const inicial = nombre.trim().charAt(0).toUpperCase() || 'U'

  return (
    <>
      <aside
        className={`sidebar-gm${collapsed ? ' collapsed' : ''}${isOpen ? ' show' : ''}`}
        id="sidebarGM"
      >
        {/* Workspace */}
        <Link to="/" className="sidebar-workspace" onClick={onClose}>
          <div className="ws-logo" aria-label="Grupo Mecsa"></div>
          <div className="ws-info">
            <div className="ws-name">Grupo Mecsa</div>
            <div className="ws-badge">TICKETS V1.1</div>
          </div>
        </Link>

        {/* Acción principal */}
        <Link to="/nuevo-ticket" className="sidebar-new-btn" onClick={onClose}>
          <i className="fas fa-plus"></i>
          <span>Nuevo ticket</span>
        </Link>

        {/* Navegación */}
        <div className="sidebar-scroll" id="sidebarNav">
          {NAV.map((bloque, i) =>
            bloque.tipo === 'links' ? (
              <div className="sidebar-section" key={`s${i}`}>
                {bloque.items.map((it) => enlace(it))}
              </div>
            ) : (
              <div className="sidebar-group" key={bloque.id}>
                <button
                  type="button"
                  className={`sidebar-group-header${bloque.items.some((i2) => esActivo(i2.to)) ? ' has-active' : ''}`}
                  onClick={() => abrirGrupo(bloque.id)}
                  aria-expanded={grupoAbierto === bloque.id}
                  aria-controls={bloque.id}
                >
                  <i className="fas fa-chevron-right group-chevron"></i>
                  <span className="group-title">{bloque.titulo}</span>
                </button>
                <div
                  className={`sidebar-submenu collapse${grupoAbierto === bloque.id ? ' show' : ''}`}
                  id={bloque.id}
                >
                  {bloque.items.map((it) => enlace(it, true))}
                </div>
              </div>
            )
          )}
        </div>

        {/* Usuario / cerrar sesión */}
        <a
          role="button"
          tabIndex={0}
          className="sidebar-user"
          title="Cerrar sesión"
          onClick={cerrarSesion}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') cerrarSesion()
          }}
        >
          {employee?.photo ? (
            <div
              className="user-avatar"
              style={{ background: 'none', padding: 0, overflow: 'hidden' }}
            >
              <img
                src={employee.photo}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 'inherit',
                }}
              />
            </div>
          ) : (
            <div className="user-avatar">{inicial}</div>
          )}
          <div className="user-details">
            <div className="user-name">{nombre.split(' ')[0]}</div>
            <div className="user-role">Cerrar sesión</div>
          </div>
          <span className="user-settings ms-auto">
            <i className="fas fa-arrow-right-from-bracket"></i>
          </span>
        </a>
      </aside>

      <div
        className={`sidebar-overlay${isOpen ? ' show' : ''}`}
        onClick={onClose}
      ></div>
    </>
  )
}

export default Sidebar
